import React, { useState, useEffect } from "react";
import { Table, Button, Select } from "antd";
import styled from "styled-components";
import { DatePicker } from "antd";
import { Card, CardHeader, CardBody } from "reactstrap";
import Axios from "axios";
import moment from "moment";

import { Page } from "../../components";
import { useMst } from "../../store/RootStore";
import DashboardLayout from "../../layouts/Dashboard";
import Loader from "../../components/Loader";

const { RangePicker } = DatePicker;

const columns = [
  {
    title: "PTAF No",
    dataIndex: "ptaf_no",
    render: (value: string) => value,
  },
  {
    title: "Date",
    dataIndex: "date",
    render: (date: string) => date,
  },
  {
    title: "Transfer Type",
    dataIndex: "transfer_type",
    render: (value: string) => value,
  },
  {
    title: "Issued By",
    dataIndex: "issued_by",
    render: (value: string) => value,
  },
  {
    title: "Issued to",
    dataIndex: "issued_to",
    render: (value: string) => value,
  },
  {
    title: "Asset No",
    dataIndex: "asset_no",
    render: (value: string) => value,
  },
  {
    title: "Model No",
    dataIndex: "model_no",
    render: (value: string) => value,
  },
  {
    title: "Serial No",
    dataIndex: "serial_no",
    render: (value: string) => value,
  },
  {
    title: "Item Description",
    dataIndex: "item_description",
    render: (value: string) => value,
  },
  {
    title: "Status",
    dataIndex: "status",
    render: (value: string) => value,
  },
];

const Wrapper = styled.div``;

const PropertyTransfer: React.FC = () => {
  const store = useMst();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);

  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedStores, setSelectedStores] = useState<any[]>([]);

  const handleDateRangeChange = (values: any, valuesStr: any) => {
    if (selectedStores.includes("all")) {
      const startOfMonth = moment(values).startOf("month").format("YYYY-MM-DD");
      const endOfMonth = moment(values).endOf("month").format("YYYY-MM-DD");

      setFrom(startOfMonth);
      setTo(endOfMonth);
    } else {
      setFrom(valuesStr[0]);
      setTo(valuesStr[1]);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const token = store.userAccount.token;
      const response = await Axios.get("/api/references/stores/list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLoading(false);
      setStores(response.data);

      if (response.data.length === 1) {
        setSelectedStores([response.data[0].id]);
      }
    })();
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    const token = store.userAccount.token;
    const response = await Axios.get(
      "/api/reports/property_transfers/summary",
      {
        params: {
          from,
          to,
          stores: selectedStores.join(","),
          status: selectedStatus,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    console.log("report", response);
    setLoading(false);
    setData(response.data.data);
  };

  const handleSelectStore = (values: any) => {
    const lastSelected = values[values.length - 1];

    if (lastSelected === "all") {
      setSelectedStores(["all"]);
    } else {
      setSelectedStores(values.filter((el) => el !== "all"));
    }
  };

  const handleExport = async () => {
    const token = store.userAccount.token;
    const response = await Axios.get("/api/reports/property_transfers/export", {
      responseType: "blob",
      params: {
        from,
        to,
        stores: selectedStores.join(","),
        status: selectedStatus,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "ptaf-summary.csv");
    document.body.appendChild(link);
    link.click();
  };

  return (
    <DashboardLayout>
      <Page>
        <Card style={{ zIndex: 2 }}>
          <CardHeader>PTAF Summary</CardHeader>
          <CardBody>
            <Wrapper>
              <p className="card-title"></p>
              <p className="title">Filters:</p>
              <div className="mt-3">
                <Select
                  className="mr-2"
                  style={{ minWidth: 300 }}
                  placeholder="Select store/s"
                  mode="multiple"
                  value={selectedStores}
                  onChange={handleSelectStore}
                >
                  <Select.Option value="all">All Stores</Select.Option>
                  {stores.map((store: any) => (
                    <Select.Option
                      key={store.id}
                      value={store.id}
                      disabled={
                        selectedStores.length >= 3
                          ? selectedStores.includes(store.id)
                            ? false
                            : true
                          : false
                      }
                    >
                      {store.label}
                    </Select.Option>
                  ))}
                </Select>
                {selectedStores.includes("all") ? (
                  <DatePicker picker="month" onChange={handleDateRangeChange} />
                ) : (
                  <RangePicker
                    className="mr-2"
                    onChange={handleDateRangeChange}
                  />
                )}
                <Select
                  allowClear={true}
                  className="mr-2"
                  style={{ width: 200 }}
                  placeholder="Filter by Status"
                  value={selectedStatus ?? undefined}
                  onChange={(value: string) => setSelectedStatus(value)}
                >
                  <Select.Option value="0">Pending</Select.Option>
                  <Select.Option value="10">Approved by ARL</Select.Option>
                  <Select.Option value="15">Approved by RDO</Select.Option>
                  <Select.Option value="20">Approved</Select.Option>
                  <Select.Option value="-1">Rejected</Select.Option>
                </Select>
              </div>
              <div className="mt-3">
                <Button className="mr-5" type="primary" onClick={handleSearch}>
                  Search
                </Button>
                {data.length > 0 && (
                  <Button type="primary" onClick={handleExport}>
                    Export
                  </Button>
                )}
              </div>
            </Wrapper>
          </CardBody>
        </Card>
        <Card style={{ zIndex: 2 }}>
          <CardBody>
            <Wrapper>
              {loading ? (
                <Loader />
              ) : (
                <Table
                  columns={columns}
                  rowKey={(v) => v.id}
                  dataSource={data}
                  size="small"
                ></Table>
              )}
            </Wrapper>
          </CardBody>
        </Card>
      </Page>
    </DashboardLayout>
  );
};

export default PropertyTransfer;
